import React, { useEffect, useState } from "react";
import { Shield, Users, CalendarDays, UserCheck, KeyRound, Plus, Copy, Trash2, UserX, Clock, MapPin } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CHART_COLORS } from "@/lib/format";
import client from "@/lib/api";
import { toast } from "sonner";

const DURATIONS = [
  { value: 0, label: "Illimité" }, { value: 3, label: "3 mois" }, { value: 6, label: "6 mois" },
  { value: 9, label: "9 mois" }, { value: 12, label: "12 mois" },
];
const isExpired = (c) => c.expiresAt && new Date(c.expiresAt) < new Date();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Metric = ({ icon: Icon, label, value, sub, tint }) => (
  <div className="bg-card rounded-xl border border-border p-5" data-testid={`metric-${label}`}>
    <div className="flex items-center justify-between">
      <div><p className="text-sm text-muted-foreground">{label}</p><p className="font-display text-3xl font-bold mt-1">{value ?? "—"}</p>{sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}</div>
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${tint}`}><Icon className="h-5 w-5" /></span>
    </div>
  </div>
);

function PieCard({ title, data, testid }) {
  const has = (data || []).some((d) => d.value > 0);
  return (
    <div className="bg-card rounded-xl border border-border p-5" data-testid={testid}>
      <h3 className="font-display font-semibold mb-4">{title}</h3>
      {has ? (
        <div className="flex items-center gap-4">
          <ResponsiveContainer width="55%" height={220}>
            <PieChart><Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>{data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-2">{data.map((r, i) => (<div key={r.name} className="flex items-center gap-2 text-sm"><span className="h-3 w-3 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="flex-1 truncate">{r.name}</span><b>{r.value}</b></div>))}</div>
        </div>
      ) : <p className="text-sm text-muted-foreground py-10 text-center">Aucune donnée disponible.</p>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [ok, setOk] = useState(null);
  const [months, setMonths] = useState(6);
  const [maxUses, setMaxUses] = useState(1);
  const [eventQuota, setEventQuota] = useState(0);
  const [guestQuota, setGuestQuota] = useState(0);
  const [statMonth, setStatMonth] = useState("");

  const load = () => Promise.all([client.get(`/admin/stats${statMonth ? `?month=${statMonth}` : ""}`), client.get("/admin/users"), client.get("/admin/signup-codes")])
    .then(([s, u, c]) => { setStats(s.data); setUsers(u.data); setCodes(c.data); setOk(true); })
    .catch(() => setOk(false));

  useEffect(() => { load(); }, []);
  useEffect(() => { client.get(`/admin/stats${statMonth ? `?month=${statMonth}` : ""}`).then(({ data }) => setStats(data)).catch(() => {}); }, [statMonth]);

  const genCode = async () => { try { const { data } = await client.post("/admin/signup-codes", { maxUses: Math.max(0, parseInt(maxUses) || 0), validityMonths: months, eventQuota: Math.max(0, parseInt(eventQuota) || 0), guestQuota: Math.max(0, parseInt(guestQuota) || 0) }); setCodes((c) => [data, ...c]); toast.success("Code généré"); } catch { toast.error("Génération impossible"); } };
  const toggleCode = async (code, active) => { try { await client.post(`/admin/signup-codes/${code}/${active ? "revoke" : "activate"}`); setCodes((c) => c.map((x) => x.code === code ? { ...x, active: !active } : x)); } catch { toast.error("Action impossible"); } };
  const delCode = async (code) => { try { await client.delete(`/admin/signup-codes/${code}`); setCodes((c) => c.filter((x) => x.code !== code)); toast.success("Code supprimé"); } catch { toast.error("Suppression impossible"); } };
  const delUser = async (uid, email) => { if (!window.confirm(`Supprimer le compte ${email} et toutes ses données ?`)) return; try { await client.delete(`/admin/users/${uid}`); setUsers((u) => u.filter((x) => x.user_id !== uid)); toast.success("Compte supprimé"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); } };

  if (ok === false || (user && !user.is_admin)) return (<><PageHeader title="Espace admin" /><div className="bg-card rounded-xl border border-border p-10 text-center" data-testid="admin-denied"><Shield className="h-8 w-8 mx-auto text-slate-300 mb-2" /><p className="text-muted-foreground">Accès réservé à l'administrateur.</p></div></>);

  return (
    <>
      <PageHeader title="Espace admin" subtitle="Gérez les comptes, les codes d'accès et suivez l'activité de la plateforme" />

      <div className="flex items-center justify-end mb-3">
        <label className="text-xs text-muted-foreground mr-2">Mois des événements créés</label>
        <input type="month" value={statMonth} onChange={(e) => setStatMonth(e.target.value)} data-testid="admin-month-picker" className="h-9 rounded-lg border border-border px-2 text-sm" />
        {statMonth && <button onClick={() => setStatMonth("")} className="text-xs text-muted-foreground underline ml-2" data-testid="admin-month-reset">Mois en cours</button>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="admin-stats">
        <Metric icon={UserCheck} label="Organisateurs actifs" value={stats ? `${stats.organizers_active}/${stats.organizers_total}` : "—"} sub="Codes non expirés / total" tint="bg-indigo-100 text-indigo-600" />
        <Metric icon={CalendarDays} label="Événements créés" value={stats?.events_month} sub={stats ? `ce mois-ci (${stats.month_label})` : ""} tint="bg-violet-100 text-violet-600" />
        <Metric icon={Users} label="Participants inscrits" value={stats?.participants} sub="Toutes éditions" tint="bg-emerald-100 text-emerald-600" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <PieCard title="Organisateurs par type" data={stats?.org_by_type || []} testid="pie-org-type" />
        <PieCard title="Événements par type" data={stats?.ev_by_type || []} testid="pie-ev-type" />
      </div>

      <div className="bg-card rounded-xl border border-border p-5 mb-6" data-testid="cities-table">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><MapPin className="h-4 w-4" />Événements par ville</h3>
        {(stats?.cities || []).length ? (
          <Table>
            <TableHeader><TableRow><TableHead>Ville</TableHead><TableHead className="text-right">Nombre d'événements</TableHead></TableRow></TableHeader>
            <TableBody>{stats.cities.map((c) => (<TableRow key={c.city}><TableCell>{c.city}</TableCell><TableCell className="text-right font-semibold">{c.count}</TableCell></TableRow>))}</TableBody>
          </Table>
        ) : <p className="text-sm text-muted-foreground py-4">Aucun événement.</p>}
      </div>

      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Users className="h-4 w-4" />Comptes utilisateurs ({users.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2 pr-3">Raison sociale</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Code d'accès</th><th className="py-2 pr-3">Début validité</th><th className="py-2 pr-3">Fin validité</th><th className="py-2"></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-border/60" data-testid={`admin-user-${u.user_id}`}>
                  <td className="py-2 pr-3 font-medium">{u.companyName || "—"}{u.is_admin && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Admin</span>}{u.orgType && <span className="block text-[11px] text-muted-foreground font-normal">{u.orgType}</span>}</td>
                  <td className="py-2 pr-3">{u.email}</td>
                  <td className="py-2 pr-3"><code className="font-mono text-xs">{u.signup_code || "—"}</code>{u.code_expired && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">Expiré</span>}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{fmtDate(u.code_start)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.code_end ? fmtDate(u.code_end) : "Sans expiration"}</td>
                  <td className="py-2 text-right">{!u.is_admin && <Button size="sm" variant="outline" onClick={() => delUser(u.user_id, u.email)} data-testid={`delete-user-${u.user_id}`}><UserX className="h-3.5 w-3.5 mr-1 text-rose-600" />Supprimer</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-display font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4" />Codes d'accès ({codes.length})</h3>
        </div>

        <div className="flex items-end gap-3 flex-wrap mb-4 p-3 rounded-lg bg-muted/40 border border-border">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Durée de validité</label>
            <div className="flex gap-1" data-testid="code-duration-group">
              {DURATIONS.map((d) => (
                <button key={d.value} onClick={() => setMonths(d.value)} data-testid={`code-duration-${d.value}`}
                  className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${months === d.value ? "bg-indigo-600 text-white border-indigo-600" : "bg-card border-border hover:bg-muted"}`}>{d.label}</button>
              ))}
            </div>
          </div>
          <div className="w-32">
            <label className="text-xs text-muted-foreground block mb-1">Nb d'utilisations</label>
            <Input type="number" min={0} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="0 = illimité" data-testid="code-maxuses-input" className="h-9" />
          </div>
          <div className="w-32">
            <label className="text-xs text-muted-foreground block mb-1">Événements max</label>
            <Input type="number" min={0} value={eventQuota} onChange={(e) => setEventQuota(e.target.value)} placeholder="0 = illimité" data-testid="code-events-input" className="h-9" />
          </div>
          <div className="w-32">
            <label className="text-xs text-muted-foreground block mb-1">Invités max</label>
            <Input type="number" min={0} value={guestQuota} onChange={(e) => setGuestQuota(e.target.value)} placeholder="0 = illimité" data-testid="code-guests-input" className="h-9" />
          </div>
          <Button size="sm" onClick={genCode} data-testid="admin-generate-code" className="h-9"><Plus className="h-4 w-4 mr-1" />Générer un code</Button>
          <p className="text-xs text-muted-foreground w-full">0 = illimité pour chaque quota. Exemple : « 1 utilisation, 3 événements max, 500 invités max, sur 6 mois ».</p>
        </div>

        <div className="space-y-2">
          {codes.length ? codes.map((c) => {
            const expired = isExpired(c);
            return (
            <div key={c.code} className="flex items-center gap-2 flex-wrap border border-border rounded-lg p-2.5" data-testid={`admin-code-${c.code}`}>
              <code className="font-mono font-semibold">{c.code}</code>
              <span className={`text-xs px-2 py-0.5 rounded-full ${expired ? "bg-rose-100 text-rose-700" : c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{expired ? "Expiré" : c.active ? "Actif" : "Révoqué"}</span>
              <span className="text-xs text-muted-foreground">{c.uses} utilisation(s){c.maxUses ? ` / ${c.maxUses} max` : " / illimité"}</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{c.expiresAt ? `expire le ${fmtDate(c.expiresAt)}` : "sans expiration"}</span>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Code copié"); }}><Copy className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => toggleCode(c.code, c.active)}>{c.active ? "Révoquer" : "Réactiver"}</Button>
                <Button size="sm" variant="ghost" onClick={() => delCode(c.code)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            </div>
          ); }) : <p className="text-sm text-muted-foreground">Aucun code. Générez-en un pour autoriser des inscriptions.</p>}
        </div>
      </div>
    </>
  );
}
